/*!
 * speakerGains.js — where a sound is, as a gain for each speaker
 * © 2026 Rob Canning — GPLv3
 *
 * The maths of speakers() (speakers.js), with no Web Audio: the node tests
 * load it. Directions are spatialMap.js's and the SuperCollider receiver's
 * (sc/oscilla-spatial.scd in the spatial demos): azimuth 0 = front,
 * positive to the LEFT, elevation 0 = ear level, in degrees.
 *
 * A ring (every speaker at ear level) pans between the two speakers either
 * side of the sound, a dome between the three around it: VBAP, constant
 * power. dist: 1 is the sound at the speakers, 0 in the middle -- every
 * speaker alike, as the receiver spreads it.
 */

// the receiver's layouts, in output order
export const LAYOUTS = {
  // two speakers: a fold-down for headphones or a desk (left-right only)
  stereo: [[30, 0], [-30, 0]],
  // 1 FL  2 FR  3 RR  4 RL (clockwise)
  quad: [[45, 0], [-45, 0], [-135, 0], [135, 0]],
  // ring of 8, clockwise from front-left
  octo: [[22.5, 0], [-22.5, 0], [-67.5, 0], [-112.5, 0],
         [-157.5, 0], [157.5, 0], [112.5, 0], [67.5, 0]],
  // 4 at ear level (FL FR RR RL) + 4 above at 45° (same order)
  dome8: [[45, 0], [-45, 0], [-135, 0], [135, 0],
          [45, 45], [-45, 45], [-135, 45], [135, 45]],
  // IEM CUBE (Graz), 12 + 8 + 4, numbered clockwise from the front: from the
  // 2008 survey (the 24-channel demo's resources/), check before a concert
  cube24: [
    [-0.1, -0.5], [-23.7, 0.0], [-48.0, 0.2], [-72.3, 0.3], [-102.7, 0.3], [-138.2, -0.2],
    [-179.7, -0.1], [138.1, -0.3], [100.6, 0.2], [69.4, 0.0], [44.6, 0.1], [21.2, 0.1],
    [-22.6, 27.9], [-67.5, 28.0], [-113.8, 27.6], [-157.5, 28.4],
    [156.3, 28.5], [112.9, 28.2], [65.0, 28.1], [22.4, 27.4],
    [-46.2, 56.3], [-132.4, 56.9], [132.7, 56.7], [42.6, 57.1],
  ],
};

const RAD = Math.PI / 180;

/** A direction as a unit vector: x front, y left, z up. */
export function unitVector(azi, elev = 0) {
  const a = azi * RAD, e = elev * RAD;
  return [Math.cos(a) * Math.cos(e), Math.sin(a) * Math.cos(e), Math.sin(e)];
}

// a 3x3 matrix's inverse (rows), or null when the three are (nearly) in a plane
function invert3(m) {
  const [a, b, c] = m[0], [d, e, f] = m[1], [g, h, i] = m[2];
  const A = e * i - f * h, B = f * g - d * i, C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-3) return null;
  return [
    [A / det, (c * h - b * i) / det, (b * f - c * e) / det],
    [B / det, (a * i - c * g) / det, (c * d - a * f) / det],
    [C / det, (b * g - a * h) / det, (a * e - b * d) / det],
  ];
}

function invert2(m) {
  const [[a, b], [c, d]] = m;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-6) return null;
  return [[d / det, -b / det], [-c / det, a / det]];
}

const angleBetween = (u, v) => Math.acos(Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1] + u[2] * v[2])));

/**
 * A layout made ready to pan over: its speakers' vectors and the pairs (a
 * ring) or triangles (a dome) VBAP chooses between, smallest first -- the
 * first that holds a sound is the one it sounds in.
 *
 * @param {Array<[number, number]>} speakers  [azimuth, elevation] per output
 */
export function prepareLayout(speakers) {
  const n = speakers.length;
  const vecs = speakers.map(([a, e]) => unitVector(a, e));
  const is3D = speakers.some(([, e]) => Math.abs(e) > 1);   // as the receiver decides
  const sets = [];
  if (n <= 2) return { n, speakers, vecs, is3D: false, sets, stereo: true };
  if (!is3D) {
    // a ring: each speaker and the next one round, in azimuth order
    const order = speakers.map((s, i) => i).sort((i, j) => speakers[i][0] - speakers[j][0]);
    for (let k = 0; k < n; k++) {
      const i = order[k], j = order[(k + 1) % n];
      const inv = invert2([[vecs[i][0], vecs[i][1]], [vecs[j][0], vecs[j][1]]]);
      if (inv) sets.push({ idx: [i, j], inv, size: angleBetween(vecs[i], vecs[j]) });
    }
  } else {
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) for (let k = j + 1; k < n; k++) {
      const inv = invert3([vecs[i], vecs[j], vecs[k]]);
      if (!inv) continue;
      const size = angleBetween(vecs[i], vecs[j]) + angleBetween(vecs[j], vecs[k]) + angleBetween(vecs[i], vecs[k]);
      sets.push({ idx: [i, j, k], inv, size });
    }
  }
  sets.sort((a, b) => a.size - b.size);
  return { n, speakers, vecs, is3D, sets, stereo: false };
}

/**
 * The gain of each of a layout's speakers for a sound at (azi, elev), dist
 * from the middle: their squares add up to 1.
 *
 * Two speakers (stereo, or a bigger layout folded down to a stereo output)
 * pan left-right by the side the sound is on, nearer the middle as it comes
 * in, as the receiver's Pan2 does.
 */
export function speakerGains(layout, azi = 0, elev = 0, dist = 1) {
  const n = layout.n;
  const d = Math.max(0, Math.min(1, Number.isFinite(dist) ? dist : 1));
  const p = unitVector(azi, layout.is3D ? elev : 0);
  let g;
  if (layout.stereo) {
    const pos = Math.max(-1, Math.min(1, -p[1] * (0.4 + 0.6 * d)));   // -1 left
    const th = (pos + 1) * Math.PI / 4;
    return n === 1 ? [1] : [Math.cos(th), Math.sin(th)];
  }
  g = vbap(layout, p);
  // dist: from the point (1) to everywhere at once (0), the power kept
  if (d < 1) g = g.map(x => Math.sqrt(d * x * x + (1 - d) / n));
  return g;
}

function vbap(layout, p) {
  const { n, sets, is3D } = layout;
  let best = null, bestMin = -Infinity;
  for (const s of sets) {
    const w = is3D
      ? [0, 1, 2].map(r => p[0] * s.inv[0][r] + p[1] * s.inv[1][r] + p[2] * s.inv[2][r])
      : [0, 1].map(r => p[0] * s.inv[0][r] + p[1] * s.inv[1][r]);
    const lo = Math.min(...w);
    if (lo >= -1e-9) { best = { s, w }; break; }       // the smallest set holding it
    if (lo > bestMin) { bestMin = lo; best = { s, w }; }   // none does (below a dome): the nearest
  }
  const g = new Array(n).fill(0);
  if (!best) { g[0] = 1; return g; }
  const w = best.w.map(x => Math.max(0, x));
  const norm = Math.hypot(...w) || 1;
  best.s.idx.forEach((i, k) => { g[i] = w[k] / norm; });
  return g;
}
